import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { LoadingPlaceholder } from '@twigeducation/ts-fe-components';
import { CloudinaryImageWithInterchange } from '@twigeducation/react-cloudinary-image';
import { PageError, NotFound } from '@twigeducation/allsorts';
import getGameType from '../../apps/Game/helpers/getGameType';
import DownloadButton from '../DownloadButton';
import { getDownloadVisualOptions } from '../../apps/Visual/Visual';
import assetProptypes from './assetProptypes';
import AssetComponentRenderer from './AssetComponentRenderer';
import GameProvider from '../GameProvider';
import QuizProvider from '../QuizProvider';
import AudioProvider from '../AudioProvider';
import VideoProvider from '../VideoProvider';
import Transcripts from '../Transcripts';
import useElementSize from '../../hooks/useElementSize';
import { useTranslation } from '../../hooks/useTranslation';
import './scss/Asset.scss';

const StyledTranscripts = styled(Transcripts)`
    border-top: 1px solid #eee;
    padding: 0 2rem;
`;

export function useAlternativeVersions(id, embeds) {
    // if we have an embed then we're in English and expect the embed to have alternative versions
    const embed = embeds.find(e => e.id === id);
    if (embed) {
        return { alternativeVersions: embed.alternativeVersions, assetLanguage: embed.language };
    }

    // We've probably been given a non-English asset so we need to find the original embed
    // strip off the first 4 characters ""USEN" for example to get the resource ID
    const originalLanguage = embeds.find(e => e.id.slice(4) === id.slice(4));

    if (originalLanguage) {
        return {
            alternativeVersions: [originalLanguage],
            assetLanguage: originalLanguage.alternativeVersions[0].language,
        };
    }

    return { alternativeVersions: [], assetLanguage: null };
}

const Asset = ({
    ariaLabelledBy,
    asset: assetInfo,
    embeds,
    Instructions,
    maxHeight,
    Quiz,
    savedAnswers,
    setIsisFullScreenReadAloud,
}) => {
    const supportedLanguages = useSelector(store => store.i18n.supportedLanguages);
    const [Component, setComponent] = useState(() => () => <LoadingPlaceholder />);
    const [transcripts, setTranscripts] = useState([]);
    const { alternativeVersions, assetLanguage } = useAlternativeVersions(assetInfo.id, embeds);
    const transcriptsRef = useRef({});
    transcriptsRef.current = transcripts;
    const modalRef = useRef(null);
    const modalSize = useElementSize(modalRef);
    const videoHeight = modalSize.width ? (modalSize.width * 9) / 16 : 400;

    const getComponentWrapperStyle = mediaType =>
        mediaType === 'film' ? { height: `${videoHeight}px`, maxHeight } : { height: '100%' };

    const { t } = useTranslation();

    const getAssetComponent = async (mediaType, id) => {
        let AssetComponent;

        switch (mediaType) {
            case 'activitySheet':
            case 'file': {
                const module = await import('../FileProvider');
                const FileProvider = module.default;

                // eslint-disable-next-line react/prop-types
                AssetComponent = ({ asset }) => (
                    <div className="asset-content__main-file">
                        <FileProvider
                            id={id}
                            title={id === asset.id ? asset.title : null}
                            fileUrl={id === asset.id ? asset.fileUrl : null}
                            displayedInModal
                            setIsisFullScreenReadAloud={setIsisFullScreenReadAloud}
                        />
                    </div>
                );

                break;
            }
            case 'audio': {
                AssetComponent = () => (
                    <div className="asset__audio">
                        <AudioProvider id={id} verticalResponsive />
                    </div>
                );
                break;
            }
            case 'film': {
                AssetComponent = () => (
                    <VideoProvider
                        id={id}
                        Loading={() => <LoadingPlaceholder />}
                        NotFound={NotFound}
                        onDataLoaded={film => {
                            const filmTranscripts = [
                                ...film.alternativeVersions.map(version => ({
                                    id: version.id,
                                    languageCode: version.language.code,
                                    languageName: version.language.name,
                                    transcript: version.transcript,
                                })),
                            ].filter(transcript => transcript.transcript !== '');
                            if (film.transcript) {
                                filmTranscripts.unshift({
                                    id: film.id,
                                    languageCode: film.language.code,
                                    languageName: film.language.name,
                                    transcript: film.transcript,
                                });
                            }
                            if (transcriptsRef.current.length === 0 && filmTranscripts.length !== 0) {
                                setTranscripts(filmTranscripts);
                            }
                        }}
                        PageError={PageError}
                        theme={{ focusColor: '#ff126d' }}
                        verticalResponsive
                        ariaLabelledBy={ariaLabelledBy}
                    />
                );
                break;
            }
            case 'game': {
                AssetComponent = ({ asset }) => (
                    <GameProvider
                        id={id}
                        type={getGameType(asset.classification.contentCategory)}
                        Loading={() => <LoadingPlaceholder />}
                        NotFound={NotFound}
                        PageError={PageError}
                    />
                );
                break;
            }
            case 'interactive': {
                const module = await import('../InteractiveProvider');
                const InteractiveProvider = module.default;

                AssetComponent = () => (
                    <div className="asset__interactive">
                        <InteractiveProvider id={id} />
                    </div>
                );
                break;
            }
            case 'quiz': {
                AssetComponent = () => (
                    <Quiz
                        id={id}
                        Loading={() => <LoadingPlaceholder />}
                        NotFound={NotFound}
                        PageError={PageError}
                        savedAnswers={savedAnswers}
                    />
                );
                break;
            }
            case 'image': {
                // eslint-disable-next-line react/prop-types
                AssetComponent = ({ asset }) => (
                    <div className="asset__image">
                        <CloudinaryImageWithInterchange asset={asset} />
                    </div>
                );
                break;
            }
            case 'visual': {
                const { VisualProvider } = await import('@twigeducation/react-visuals');
                const { cloudinary } = await import('@twigeducation/react-cloudinary-image');

                // eslint-disable-next-line react/prop-types
                AssetComponent = ({ asset }) => {
                    const downloadVisualUrl = image =>
                        cloudinary.url(
                            image.cloudinary.publicId,
                            getDownloadVisualOptions(image.cloudinary.overlay.watermark),
                        );
                    const downloadButton = image => (
                        <DownloadButton
                            fileUrl={downloadVisualUrl(image)}
                            title={image.title}
                            fileExtension="jpg"
                            buttonText={t('general.visual_provider_download_btn', 'Download')}
                            position="absolute"
                        />
                    );
                    return (
                        <div className="asset__visual" data-at="asset__visual-wrapper">
                            <VisualProvider
                                id={id}
                                type={asset.classification.contentCategory}
                                Loading={() => <LoadingPlaceholder />}
                                NotFound={NotFound}
                                PageError={PageError}
                                options={{ crop: 'fit' }}
                                download={downloadButton}
                            />
                        </div>
                    );
                };
                break;
            }
            default:
                AssetComponent = () => <div data-at="asset__not-found">Classification not found</div>;
                break;
        }
        return AssetComponent;
    };

    useEffect(() => {
        let Comp;
        try {
            // eslint-disable-next-line react/prop-types
            Comp = ({ asset, versionId }) => (
                <AssetComponentRenderer asset={asset} getAssetComponent={getAssetComponent} versionId={versionId} />
            );
        } catch (e) {
            Comp = () => <div data-at="asset__error">Sorry, there was a problem loading this asset.</div>;
        }
        setComponent(() => Comp);
    }, [assetInfo.id]);

    if (alternativeVersions.length > 0 && assetLanguage) {
        return (
            <div className="asset-content" data-at="asset-tabs-wrapper" ref={modalRef}>
                <Tabs
                    defaultIndex={assetLanguage ? 0 : 1}
                    selectedTabPanelClassName="asset-content__tabs-panel--selected"
                    className="asset-content__tabs"
                    data-at="asset-tabs"
                >
                    <div className="asset-content__tabs-wrapper">
                        <TabPanel data-at="asset-tabs__panel">
                            <div style={getComponentWrapperStyle(assetInfo.classification.mediaType)}>
                                <Instructions />
                                <Component asset={assetInfo} versionId={assetInfo.id} />
                            </div>
                        </TabPanel>
                        {alternativeVersions.map(({ versionId }) => (
                            <TabPanel key={versionId} data-at="asset-tabs__panel">
                                <div style={getComponentWrapperStyle(assetInfo.classification.mediaType)}>
                                    <Instructions />
                                    <Component asset={assetInfo} versionId={versionId} />
                                </div>
                            </TabPanel>
                        ))}
                        <TabList className="asset-content__tabs-list" data-at="asset-tabs__list">
                            <Tab className="asset-content__tabs-tab" data-at="asset-tabs__item">
                                {assetLanguage.englishName}
                            </Tab>
                            {alternativeVersions
                                .filter(alternateVersion =>
                                    supportedLanguages.some(
                                        supportedLanguage =>
                                            supportedLanguage.code === alternateVersion.language.ietfTag,
                                    ),
                                )
                                .map(alternativeVersion => (
                                    <Tab
                                        key={alternativeVersion.versionId}
                                        className="asset-content__tabs-tab"
                                        data-at="asset-tabs__item"
                                    >
                                        {alternativeVersion.language.englishName}
                                    </Tab>
                                ))}
                        </TabList>
                    </div>
                </Tabs>
                {assetInfo.classification.mediaType === 'film' && (
                    <StyledTranscripts transcripts={transcripts} film={{ id: assetInfo.id, slug: assetInfo.slug }} />
                )}
            </div>
        );
    }

    return (
        <div className="asset-content" data-at="asset-content" ref={modalRef}>
            <Instructions />
            <div style={getComponentWrapperStyle(assetInfo.classification.mediaType)}>
                <Component asset={assetInfo} versionId={assetInfo.id} />
            </div>
            {assetInfo.classification.mediaType === 'film' && (
                <StyledTranscripts transcripts={transcripts} film={{ id: assetInfo.id, slug: assetInfo.slug }} />
            )}
        </div>
    );
};

Asset.defaultProps = {
    ariaLabelledBy: undefined,
    Instructions: () => null,
    maxHeight: '75vh',
    Quiz: QuizProvider,
    setIsisFullScreenReadAloud: () => {},
};

Asset.propTypes = {
    ariaLabelledBy: PropTypes.string,
    asset: assetProptypes.isRequired,
    embeds: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    Instructions: PropTypes.elementType,
    maxHeight: PropTypes.string,
    Quiz: PropTypes.elementType,
    savedAnswers: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]),
    setIsisFullScreenReadAloud: PropTypes.func,
};

export default Asset;
